/**
 * Helpers for operator-editable regex pattern STRINGS
 * (`bat.blockchain_networks.address_pattern` / `txid_pattern`,
 * `bat.vm_families.default_*`): a never-throwing compiler and a structural
 * anchoring check.
 *
 * A plain `startsWith('^') && endsWith('$')` check is defeated by a TOP-LEVEL
 * alternation: `^a|b$` parses as `(^a)|(b$)`, so `"xxxb"` matches even though
 * the string "looks anchored". This helper is the ONE definition shared by the
 * backend zod validators, the frontend live pattern tester and the seed
 * validators, so the three layers can never disagree about what "anchored"
 * means.
 */

/** Longest pattern string any layer accepts (mirrors the admin zod `.max(500)`). */
export const REGEX_PATTERN_MAX_LENGTH = 500

/**
 * Longest wallet address any layer feeds to an operator-editable regex. ReDoS
 * guard: no supported address format exceeds 90 chars (Solana ≤ 44, EVM 42,
 * bech32m ≤ 90, Cardano ≈ 104 is the only outlier and still fits). ONE value
 * for the backend chain validator, the shared `validateAddressAgainstPattern`
 * and the frontend live pattern tester, so an address the admin form accepts
 * is never one the runtime refuses on length alone.
 */
export const WALLET_ADDRESS_MAX_LENGTH_FOR_REGEX = 128

/**
 * Compile an operator-supplied pattern string, or return `null` — NEVER throws.
 * `null` for: the empty string (`new RegExp('')` would match everything),
 * a pattern longer than `maxLength` (refused before the engine sees it), or a
 * syntax error. The ONE compile helper shared by the backend address validator,
 * the zod pattern schemas, both seed validators, the shared address check and
 * the frontend live pattern tester, so "does this pattern compile" has a single
 * answer across layers.
 */
export function compileRegExpPatternOrNull(
  pattern: string,
  maxLength: number = REGEX_PATTERN_MAX_LENGTH,
): RegExp | null {
  if (pattern.length === 0 || pattern.length > maxLength) return null
  try {
    return new RegExp(pattern)
  } catch {
    return null
  }
}

/**
 * True when `pattern` starts with `^`, ends with an UNESCAPED `$`, and carries
 * no top-level `|` (an alternation inside parentheses or a character class is
 * fine — `^(a|b)$` stays effectively anchored, `^a|b$` does not).
 */
export function isEffectivelyAnchoredPattern(pattern: string): boolean {
  if (pattern.length < 2 || !pattern.startsWith('^') || !pattern.endsWith('$')) {
    return false
  }

  // The final `$` only anchors when it is not escaped: count the backslashes
  // immediately before it — an ODD count means `\$` (a literal dollar sign).
  let trailingBackslashes = 0
  for (let i = pattern.length - 2; i >= 0 && pattern[i] === '\\'; i--) {
    trailingBackslashes++
  }
  if (trailingBackslashes % 2 === 1) return false

  // Scan for a top-level `|`, tracking unescaped parenthesis depth and
  // character classes (`|` inside `[...]` is a literal pipe, not alternation).
  let parenDepth = 0
  let escaped = false
  let inCharacterClass = false
  for (const char of pattern) {
    if (escaped) {
      escaped = false
      continue
    }
    if (char === '\\') {
      escaped = true
      continue
    }
    if (inCharacterClass) {
      if (char === ']') inCharacterClass = false
      continue
    }
    if (char === '[') {
      inCharacterClass = true
    } else if (char === '(') {
      parenDepth++
    } else if (char === ')') {
      parenDepth = Math.max(0, parenDepth - 1)
    } else if (char === '|' && parenDepth === 0) {
      return false
    }
  }
  return true
}

/**
 * A stored pattern must be anchored (`^…$`, no top-level `|`) AND compile.
 * Returns the human-readable problem, or `null` when the pattern is fine.
 * ONE definition for the vm-families seed, the blockchain-networks seed and
 * any other offline validator, so every layer reports a bad pattern with the
 * same words.
 */
export function describePatternProblem(pattern: string): string | null {
  if (!isEffectivelyAnchoredPattern(pattern)) {
    return 'must be anchored (^...$) with no top-level alternation'
  }
  if (compileRegExpPatternOrNull(pattern) === null) {
    return 'must compile as a RegExp (non-empty, <= 500 chars, valid syntax)'
  }
  return null
}
